import { Link } from "react-router-dom";
import { 
  Shield, 
  Clock, 
  FileCheck, 
  Users,
  Award,
  ArrowRight,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

const whyChooseUs = [
  {
    icon: Clock,
    title: "Responsive Communication",
    description: "We pride ourselves on quick responses and clear communication throughout the process."
  },
  {
    icon: FileCheck,
    title: "Clear, Transparent Process",
    description: "No surprises. You'll always know where things stand with your return or project."
  },
  {
    icon: Shield,
    title: "Secure Client Portal",
    description: "Upload documents safely and access your files anytime through our encrypted portal."
  },
  {
    icon: Users,
    title: "Year-Round Support",
    description: "We're not just here during tax season. Get support whenever you need it."
  }
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
                About Us
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Tax Professionals You Can Trust
              </h1>
              <p className="font-body text-lg md:text-xl text-gray-300">
                DA TAXGUY & ASSOCIATES is a NYC-based tax and accounting firm dedicated to providing professional, reliable service to individuals and small businesses.
              </p>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbWVldGluZyUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3NzAzMjEyNDh8MA&ixlib=rb-4.1.0&q=85&w=600"
                alt="Professional team"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Our Story</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-6">
              Built for Busy New Yorkers
            </h2>
            <p className="font-body text-lg text-gray-600">
              We understand that tax season can be stressful. That's why we've built a practice focused on making the process as smooth and stress-free as possible. Our secure client portal, responsive communication, and year-round support are designed to give you peace of mind.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-8">
                <h3 className="font-heading text-xl font-semibold text-navy mb-4">
                  Our Mission
                </h3>
                <p className="font-body text-gray-600">
                  To provide professional tax and accounting services with integrity, responsiveness, and a commitment to our clients' success. We believe everyone deserves access to quality financial guidance.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-8">
                <h3 className="font-heading text-xl font-semibold text-navy mb-4">
                  Our Approach
                </h3>
                <p className="font-body text-gray-600">
                  We combine traditional expertise with modern technology. Our secure portal makes document exchange easy, while our personalized service ensures you're never just a number.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Why Choose Us</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              What Sets Us Apart
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card 
                key={item.title} 
                className="bg-bone border-0 text-center hover-lift"
                data-testid={`why-us-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-8 h-8 text-bronze" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-3">
                    {item.title}
                  </h3>
                  <p className="font-body text-gray-600 text-sm">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">Credentials</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-6">
                Qualified Professionals
              </h2>
              <p className="font-body text-gray-600 mb-6">
                Our team consists of experienced tax professionals with expertise across individual and business taxation. We stay current with tax law changes and continue our education to provide the best service possible.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Award className="w-4 h-4 text-bronze" />
                  </div>
                  <span className="font-body text-gray-700">Experienced tax preparers with IRS credentials</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Award className="w-4 h-4 text-bronze" />
                  </div>
                  <span className="font-body text-gray-700">Ongoing professional education and training</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Award className="w-4 h-4 text-bronze" />
                  </div>
                  <span className="font-body text-gray-700">Expertise in individual and small business taxation</span>
                </li>
              </ul>
            </div>
            <div className="bg-navy rounded-2xl p-8 lg:p-12 text-white">
              <h3 className="font-heading text-2xl font-bold mb-6">
                Meet With Us
              </h3>
              <p className="font-body text-gray-300 mb-8">
                We'd love to learn about your tax and accounting needs. Schedule a free consultation to see if we're the right fit.
              </p>
              <div className="space-y-4">
                <Button
                  asChild
                  className="w-full bg-bronze hover:bg-bronze/90 text-navy rounded-full py-6 font-medium"
                  data-testid="about-book-btn"
                >
                  <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                    Book Free Consultation
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white hover:text-navy rounded-full py-6 font-medium"
                  data-testid="about-portal-btn"
                >
                  <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                    Client Portal Login
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Ready to Work Together?
          </h2>
          <p className="font-body text-gray-300 mb-8">
            Let's discuss how we can help with your tax and accounting needs.
          </p>
          <Button
            asChild
            className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
            data-testid="about-cta-btn"
          >
            <Link to="/contact">
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
